import { useTranslation } from "react-i18next";
import TeamLogo from "../../../components/TeamLogo";
import type { CalloutKind } from "../../../lib/broadcastApi";
import type { Match } from "../../logger/types";
import GoalFrameScene from "../../logger/components/molecules/GoalFrameScene";
import { frameToSvg, type GoalPlacement } from "../../logger/utils/goalFrame";
import { resolvePlayerLabel } from "../../logger/utils/publicHeaderEvents";
import { broadcastMinute, type Callout } from "../callouts";

interface Props {
  callout: Callout | null;
  match: Match | null;
}

export const KIND_STYLE: Record<CalloutKind, { bg: string; fg: string }> = {
  goal: { bg: "#15803d", fg: "#ffffff" },
  penalty: { bg: "#7c3aed", fg: "#ffffff" },
  yellow_card: { bg: "#facc15", fg: "#111827" },
  red_card: { bg: "#dc2626", fg: "#ffffff" },
  foul: { bg: "#c2410c", fg: "#ffffff" },
};

const OUTCOME_KEYS = new Set(["Goal", "Saved", "Blocked", "OffTarget", "Post"]);

export default function Callouts({ callout, match }: Props) {
  const { t } = useTranslation("broadcast");
  if (!callout || !match) {
    return (
      <div
        data-testid="broadcast-callouts-layer"
        style={{ width: "100%", height: "100%" }}
      />
    );
  }
  const { event, kind } = callout;
  const style = KIND_STYLE[kind];
  const team =
    event.team_id === match.home_team.team_id
      ? match.home_team
      : match.away_team;
  const player = resolvePlayerLabel(match, event.player_id);
  const placement = event.data?.goal_placement as GoalPlacement | undefined;
  const showTarget =
    (kind === "goal" || kind === "penalty") &&
    placement &&
    typeof placement.x === "number" &&
    typeof placement.y === "number";
  const outcome = String(event.data?.outcome ?? "");

  return (
    <div
      data-testid="broadcast-callouts-layer"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        // Docked under the scoreboard (top centre) so the card never covers
        // the pitch action it announces.
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 150,
        boxSizing: "border-box",
      }}
    >
      <div
        data-testid={`broadcast-callout-${kind}`}
        data-callout-kind={kind}
        data-callout-id={callout.id}
        role="status"
        style={{
          minWidth: "20em",
          maxWidth: "90%",
          borderRadius: "0.9em",
          overflow: "hidden",
          boxShadow: "0 1em 3em rgba(0,0,0,0.55)",
          background: "rgba(8, 15, 30, 0.94)",
          color: "#ffffff",
          animation: "bcast-pop 320ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <div
          style={{
            background: style.bg,
            color: style.fg,
            padding: "0.4em 1em",
            fontWeight: 900,
            letterSpacing: "0.12em",
            fontSize: "1.75em",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          {t(`callout.${kind}`)}
        </div>
        <div
          style={{
            padding: "0.8em 1.1em",
            display: "flex",
            alignItems: "center",
            gap: "0.8em",
          }}
        >
          <TeamLogo team={team} size={64} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: "1.5em" }}>
              {player || team.name}
            </div>
            <div style={{ opacity: 1, fontSize: "1.25em" }}>
              {team.short_name || team.name} ·{" "}
              {broadcastMinute(event.match_clock)}
              {kind === "penalty" && OUTCOME_KEYS.has(outcome) && (
                <span data-testid="broadcast-callout-outcome">
                  {" "}
                  · {t(`outcome.${outcome}`)}
                </span>
              )}
            </div>
          </div>
        </div>
        {showTarget && placement && (
          <div
            data-testid="broadcast-callout-target"
            style={{ padding: "0 1.1em 1em", width: "20em" }}
          >
            <GoalFrameScene
              instanceId={`bcast-${callout.id}`}
              ariaLabel={t("goalTarget")}
            >
              <GoalBall x={placement.x} y={placement.y} />
            </GoalFrameScene>
          </div>
        )}
      </div>
    </div>
  );
}

function GoalBall({ x, y }: { x: number; y: number }) {
  const { px, py } = frameToSvg(x, y);
  return (
    <g data-testid="broadcast-goal-ball">
      <circle
        cx={px}
        cy={py}
        r={26}
        fill="#ffffff"
        stroke="#0f172a"
        strokeWidth={6}
      />
      <circle cx={px} cy={py} r={8} fill="#0f172a" />
    </g>
  );
}
